<template>
  <v-container>
    <v-data-table
      data-cy="data-table-licence"
      :loading="loading"
      :headers="headers"
      :items="licences"
      sort-by="name"
      class="elevation-1"
      :search="search"
    >
      <template v-slot:top>
        <v-toolbar flat color="white" :extended="$vuetify.breakpoint.xs">
          <v-toolbar-title>{{ $t("Licences") }}</v-toolbar-title>
          <v-spacer></v-spacer>

          <v-text-field
            v-if="!$vuetify.breakpoint.xs"
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('Search')"
            clearable
            single-line
            hide-details
          ></v-text-field>
          <v-spacer></v-spacer>

          <template v-if="$vuetify.breakpoint.xs" v-slot:extension>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('Search')"
              clearable
              single-line
              hide-details
            ></v-text-field>
          </template>

          <v-btn
            data-cy="button-add-licence"
            :icon="$vuetify.breakpoint.mdAndDown"
            color="primary"
            dark
            class="mb-2"
            @click.stop="addLicence"
          >
            <span v-if="$vuetify.breakpoint.lgAndUp">{{
              $t("Buy licences")
            }}</span>
            <v-icon v-if="$vuetify.breakpoint.mdAndDown" x-large
              >mdi-plus-circle</v-icon
            >
          </v-btn>
        </v-toolbar>
        <v-row>
          <v-col cols="6" sm="3" lg="2">
            <v-btn
              data-cy="button-more-filter"
              :icon="$vuetify.breakpoint.mdAndDown"
              color="primary"
              dark
              class="mb-2 mx-5"
              @click="filterBoolean = !filterBoolean"
            >
              <span v-if="$vuetify.breakpoint.lgAndUp">{{
                $t("More filters")
              }}</span>
              <v-icon v-if="$vuetify.breakpoint.mdAndDown"
                >mdi-filter-plus</v-icon
              >
            </v-btn>
          </v-col>
          <v-col cols="6" sm="3" lg="2">
            <v-btn
              depressed
              color="secondary"
              outlined
              :loading="loadingExport"
              @click="exportLicences"
              >{{ $t("Export") }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4" v-if="filterBoolean" class="px-5">
            <v-select
              :items="clients"
              v-model="clientsFilterValue"
              item-text="name"
              clearable
              :label="$t('Customer')"
            ></v-select>
          </v-col>
          <v-col cols="4" v-if="filterBoolean" class="px-5">
            <v-select
              :items="entities"
              v-model="entitiesFilterValue"
              item-text="name"
              clearable
              :label="$t('Entities')"
            ></v-select>
          </v-col>
          <v-col cols="4" v-if="filterBoolean" class="px-5">
            <v-select
              data-cy="select-input-filter-application"
              :items="applications"
              v-model="applicationsFilterValue"
              item-text="name"
              clearable
              :label="$t('Applications')"
            ></v-select>
          </v-col>
        </v-row>
      </template>
      <template v-slot:item.planType="{ item }">{{
        formatPlan(item) | capitalize
      }}</template>
      <template v-slot:item.options="{ item }"
        ><v-chip
          v-for="(option, index) in item.options.filter((x) => x.name !== '')"
          :key="index"
          >{{ option.name }}</v-chip
        ></template
      >
      <template v-slot:item.expire="{ item }">
        <v-badge
          v-if="item.plan !== 0 && item.expire"
          color="pink"
          dot
          :value="item.is_expired"
          >{{ $d(Date.parse(item.expire)) }}</v-badge
        >
      </template>
      <template v-slot:item.actions="{ item }">
        <div v-if="item.is_attached">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon color="primary" v-on="on" @click="viewLicenceFile(item)"
                >mdi-download</v-icon
              >
            </template>
            {{ $t("Download licence") }}
          </v-tooltip>
          <v-tooltip v-if="item.trial" top>
            <template v-slot:activator="{ on }">
              <v-icon color="primary" v-on="on" @click="renewLicence(item)"
                >mdi-link-plus</v-icon
              >
            </template>
            {{ $t("Assign a standard licence") }}
          </v-tooltip>
          <v-tooltip v-if="!item.trial && item.is_expired" top>
            <template v-slot:activator="{ on }">
              <v-icon color="primary" v-on="on" @click="renewLicence(item)"
                >mdi-autorenew</v-icon
              >
            </template>
            {{ $t("Renew licence") }}
          </v-tooltip>
        </div>
        <div v-else>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon color="primary" @click="editItem(item)" v-on="on"
                >mdi-pencil</v-icon
              >
            </template>
            {{ $t("Modify licence") }}
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon color="primary" @click="attachLicence(item)" v-on="on"
                >mdi-link-plus</v-icon
              >
            </template>
            {{ $t("Attach a device") }}
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    <add-edit-licence-dialog
      v-model="dialog"
      @close="close"
    ></add-edit-licence-dialog>
    <attach-licence-dialog
      v-model="attachLicenceDialog"
    ></attach-licence-dialog>
    <renew-licence-dialog v-model="renewLicenceDialog"></renew-licence-dialog>
  </v-container>
</template>

<script>
import { saveAs } from "file-saver";
import FileSaver from "file-saver";
import snackbar from "@/mixins/snackbar";
import priceMixin from "@/mixins/priceMixin";
import { mapState, mapGetters } from "vuex";
import AddEditLicenceDialog from "@/components/AddEditLicenceDialog";
import AttachLicenceDialog from "@/components/AttachLicenceDialog";
import RenewLicenceDialog from "@/components/RenewLicenceDialog";

export default {
  name: "LicencesIndex",
  components: {
    AddEditLicenceDialog,
    AttachLicenceDialog,
    RenewLicenceDialog,
  },
  mixins: [snackbar, priceMixin],
  data() {
    return {
      loading: false,
      loadingExport: false,
      dialog: false,
      attachLicenceDialog: false,
      renewLicenceDialog: false,
      search: "",
      clientsFilterValue: "",
      filterBoolean: false,
      entitiesFilterValue: "",
      applicationsFilterValue: "",
      exportFields: [
        "Application",
        "Equipement",
        "Client",
        "Agence",
        "Plan Tarifaire",
        "Options",
        "Date d'expiration",
      ],
    };
  },
  computed: {
    ...mapState({
      reseller: (state) => state.auth.user.id,
      clients: (state) => state.clients.items,
      entities: (state) => state.auth.user?.reseller_entities ?? [],
      applications: (state) => state.applications.items,
    }),
    ...mapGetters({
      licences: "licences/getLicences",
    }),
    filterApp() {
      return this.applicationsFilterValue == "" ||
        this.applicationsFilterValue == null
        ? false
        : this.applicationsFilter;
    },
    filterCli() {
      return this.clientsFilterValue == "" || this.clientsFilterValue == null
        ? false
        : this.clientsFilter;
    },
    filterEntity() {
      return this.entitiesFilterValue == "" || this.entitiesFilterValue == null
        ? false
        : this.entitiesFilter;
    },
    headers() {
      return [
        {
          text: this.$t("Application"),
          align: "start",
          sortable: true,
          value: "application.name",
          filter: this.filterApp,
        },
        {
          text: this.$t("Device"),
          align: "start",
          sortable: true,
          value: "serial_number",
        },
        {
          text: this.$t("Customer"),
          align: "start",
          sortable: true,
          value: "client.name",
          filter: this.filterCli,
        },
        {
          text: this.$t("Entity"),
          align: "start",
          sortable: true,
          value: "reseller_entity.name",
          filter: this.filterEntity,
        },
        {
          text: this.$t("Pricing plan"),
          align: "start",
          sortable: true,
          value: "planType",
          sort: this.sortPlan,
        },
        {
          text: this.$t("Options"),
          align: "start",
          sortable: false,
          value: "options",
        },
        {
          text: this.$t("Expiration date"),
          align: "start",
          sortable: true,
          value: "expire",
        },
        {
          text: this.$t("Actions"),
          align: "left",
          value: "actions",
          sortable: false,
        },
      ];
    },
  },
  created() {
    this.loading = true;
    this.$store
      .dispatch("licences/fetchAll")
      .then(() => (this.loading = false))
      .catch(() => (this.loading = false));
  },
  methods: {
    close() {
      this.dialog = false;
    },
    addLicence() {
      this.$store.dispatch("licences/resetEditedItem");
      this.dialog = true;
    },
    editItem(item) {
      this.$store.dispatch("licences/resetEditedItem");
      this.$store.commit("licences/setEditedId", item.id);
      this.$store.commit(
        "licences/setEditedItem",
        Object.assign({ quantity: 1 }, item)
      );
      this.dialog = true;
    },
    attachLicence(item) {
      this.$store.dispatch("licences/resetEditedItem");
      this.$store.commit("licences/setSelectedItem", item);
      this.attachLicenceDialog = true;
    },
    renewLicence(item) {
      this.$store.dispatch("licences/resetEditedItem");
      this.$store.commit("licences/setSelectedItem", item);
      this.renewLicenceDialog = true;
    },
    viewLicenceFile(item) {
      const xml = item.xml;
      var blob = new Blob([xml], { type: "text/plain;charset=utf-8" });
      saveAs(blob, "" + item.serial_number + ".lic");
    },
    clientsFilter(value, search, item) {
      if (!this.clientsFilterValue) {
        return true;
      }
      return item.client?.name
        .toLowerCase()
        .includes(this.clientsFilterValue.toLowerCase());
    },
    entitiesFilter(value, search, item) {
      if (!this.entitiesFilterValue) {
        return true;
      }
      return item.reseller_entity.name
        .toLowerCase()
        .includes(this.entitiesFilterValue.toLowerCase());
    },
    applicationsFilter(value, search, item) {
      if (!this.applicationsFilterValue) {
        return true;
      }
      return (
        item.application.name.toLowerCase() ===
        this.applicationsFilterValue.toLowerCase()
      );
    },
    exportLicences() {
      this.loadingExport = true;
      try {
        var sep = "sep=,";
        var csvStr = sep + "\n" + this.exportFields.join(",") + "\n";
        this.licences.forEach((licence) => {
          var application = licence.application.name;
          var equipement =
            licence.serial_number == null ? "" : licence.serial_number;
          var client = licence.client == null ? "" : licence.client.name;
          var agence = licence.reseller_entity.name;
          var plan = this.formatPlan(licence);
          var options = licence.options
            .map((option) => option.type)
            .join(" | ");
          var date =
            licence.expire == null ? "" : this.$d(Date.parse(licence.expire));
          csvStr +=
            `"${application}"` +
            "," +
            `"${equipement}"` +
            "," +
            `"${client}"` +
            "," +
            `"${agence}"` +
            "," +
            `"${plan}"` +
            "," +
            `"${options}"` +
            "," +
            `"${date}"` +
            "\n";
        });
        //csv bom utf-8
        var blob = new Blob(["\uFEFF" + csvStr], {
          type: "text/csv;charset=utf-8;",
        });
        FileSaver.saveAs(blob, "exportLicences.csv");
        this.loadingExport = false;
      } catch (error) {
        this.loadingExport = false;
      }
    },
  },
};
</script>
